import { GroupChannel } from '@sendbird/chat/groupChannel'
import { ChatLogger, chatLoggerFactory } from '@/components/chat/ChatLogger'
import { sendUnreadMessageNotification, sentMessage } from '@/services/chat'

export type MemberReadReceiptStatus =
  // statuses used to determine
  // whether we should send messages to this user
  | 'has-not-read-messages-from-the-previous-session'
  | 'read-latest-message'
  // statuses used to signal the state of sending a notification
  | 'queued-notification'
  | 'sending-notification'
  | 'sent-notification'

const NOTIFICATION_DELAY = 10000

export class ChatMessageSentEmailNotificationHandler {
  channel: GroupChannel
  logger: ChatLogger
  currentUserId: string
  state: 'ready' | 'checking-message-read-status'
  membersReadReceiptStatusRecord: Record<string, MemberReadReceiptStatus> = {}
  membersTimeoutFnRecord: Record<string, ReturnType<typeof setTimeout>> = {}

  constructor(channel: GroupChannel, userId: string) {
    this.channel = channel
    this.currentUserId = userId
    this.logger = chatLoggerFactory.createLogger('SentEmailNotificationHandler')
    const readStatus = this.channel.getReadStatus(true)
    for (const userId in readStatus) {
      const hasReadLatestMessage = this.hasReadLatestMessage(
        readStatus[userId].readAt
      )
      this.membersReadReceiptStatusRecord[userId] = hasReadLatestMessage
        ? 'read-latest-message'
        : 'has-not-read-messages-from-the-previous-session'
    }
    this.state = 'ready'
  }

  /**
   * Function is called after a message is sent.
   *   this.currentUserId --> sender of the message
   *               member --> user in chat room
   * @returns
   */
  onSendMessage = () => {
    if (this.state !== 'ready') {
      return
    }
    for (const member of this.channel.members) {
      if (member.userId !== this.currentUserId) {
        const readStatus = this.membersReadReceiptStatusRecord[member.userId]
        sentMessage(this.currentUserId, member.userId)
        if (!readStatus || readStatus !== 'read-latest-message') {
          this.logger.debug('Queuing notification for', member.userId)
          this.queueNotification(member.userId)
        }
      }
    }
  }

  onChannelChanged = (channel: GroupChannel) => {
    this.channel = channel
  }

  onUnreadMemberStatusUpdated = (channel: GroupChannel) => {
    this.channel = channel
    this.state = 'checking-message-read-status'
    this.logger.debug('Updating messages receipts for', channel.url)
    const readStatus = this.channel.getReadStatus(true)
    for (const userId in readStatus) {
      if (userId === this.currentUserId) {
        const hasReadLatestMessage = this.hasReadLatestMessage(
          readStatus[userId].readAt
        )
        this.membersReadReceiptStatusRecord[userId] = hasReadLatestMessage
          ? 'read-latest-message'
          : this.membersReadReceiptStatusRecord[userId]
      }
    }
    this.state = 'ready'
  }

  private queueNotification = (memberUserId) => {
    this.membersTimeoutFnRecord[memberUserId] = setTimeout(
      () => this.sendSentMessageEmailNotification(memberUserId),
      NOTIFICATION_DELAY
    )
    this.membersReadReceiptStatusRecord[memberUserId] = 'queued-notification'
  }

  private sendSentMessageEmailNotification = async (memberUserId: string) => {
    if (memberUserId !== this.currentUserId) {
      if (this.state !== 'ready') {
        // If the channel has signaled that users have read message -> `onUnreadMemberStatusUpdated`
        // we need to wait until that finishes
        this.membersTimeoutFnRecord[memberUserId] = setTimeout(
          () => this.sendSentMessageEmailNotification(memberUserId),
          1000
        )
        return
      }

      const readStatus = this.membersReadReceiptStatusRecord[memberUserId]
      if (readStatus !== 'queued-notification') {
        this.logger.debug('Skipping send notification for ', memberUserId)
        return
      }

      this.logger.debug('Sending notification to', memberUserId)
      this.membersReadReceiptStatusRecord[memberUserId] = 'sending-notification'
      await sendUnreadMessageNotification(memberUserId, this.channel.url)
      this.membersReadReceiptStatusRecord[memberUserId] = 'sent-notification'
    }
    return
  }

  private hasReadLatestMessage = (lastReadReceiptTimestamp: number) => {
    if (!this.channel.lastMessage) return true
    return lastReadReceiptTimestamp >= this.channel.lastMessage?.createdAt
  }
}
